import { useEffect, useMemo, useState } from "react";
import TutorAvatar from "./TutorAvatar";
import {
  arrayFromCsv,
  downloadCsv,
  formatCurrency,
  formatDateTime,
} from "../lib/format";
import {
  deleteTutor,
  getBookings,
  getLeads,
  getTutors,
  isAdminAuthenticated,
  resetTutorsToSeed,
  setAdminAuthenticated,
  upsertTutor,
} from "../lib/storage";

const describeImageInput = (image) => {
  if (!image) {
    return "";
  }
  return image.startsWith("data:image") ? "Uploaded image ready" : image;
};

const defaultForm = {
  id: "",
  name: "",
  headline: "",
  experienceYears: 5,
  rating: 4.8,
  subjectsInput: "",
  price: 800,
  shortBio: "",
  fullBio: "",
  qualifications: "",
  category: "",
  tagsInput: "",
  availabilityInput: "Mon | 6:30 PM IST, Wed | 7:45 PM IST, Sat | 10:00 AM IST",
  image: "",
  imageInput: "",
};

const tabs = [
  { id: "tutors", label: "Tutors" },
  { id: "leads", label: "Leads" },
  { id: "bookings", label: "Bookings" },
  { id: "security", label: "Security" },
];

export default function AdminPanel() {
  const [authenticated, setAuthenticated] = useState(isAdminAuthenticated());
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [activeTab, setActiveTab] = useState("tutors");
  const [tutors, setTutors] = useState(getTutors());
  const [leads, setLeads] = useState(getLeads());
  const [bookings, setBookings] = useState(getBookings());
  const [form, setForm] = useState(defaultForm);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const refresh = () => {
    setTutors(getTutors());
    setLeads(getLeads());
    setBookings(getBookings());
  };

  useEffect(() => {
    refresh();
    const handleStorageSync = () => refresh();
    window.addEventListener("storage", handleStorageSync);
    window.addEventListener("dreameer:data-updated", handleStorageSync);
    return () => {
      window.removeEventListener("storage", handleStorageSync);
      window.removeEventListener("dreameer:data-updated", handleStorageSync);
    };
  }, []);

  const filteredTutors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return [...tutors]
      .filter((tutor) => {
        if (!query) {
          return true;
        }
        return `${tutor.name} ${tutor.subjects.join(" ")} ${tutor.shortBio}`.toLowerCase().includes(query);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchTerm, tutors]);

  const handleLogin = (event) => {
    event.preventDefault();
    if (password === "dreameer123") {
      setAdminAuthenticated(true);
      setAuthenticated(true);
      setPassword("");
      setPasswordError("");
      setMessage("Welcome back.");
      return;
    }
    setPasswordError("Incorrect password. Please try again.");
  };

  const handleLogout = () => {
    setAdminAuthenticated(false);
    setAuthenticated(false);
    setMessage("");
  };

  const resetForm = () => {
    setForm(defaultForm);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (name === "imageInput") {
      setForm((current) => ({ ...current, imageInput: value, image: value }));
      return;
    }
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleImageUpload = (event) => {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((current) => ({
        ...current,
        image: String(reader.result || ""),
        imageInput: file.name,
      }));
      setMessage(`Image selected for ${form.name || "new tutor"}. Save the form to apply it.`);
    };
    reader.readAsDataURL(file);
  };

  const handleEdit = (tutor) => {
    setForm({
      id: tutor.id,
      name: tutor.name,
      headline: tutor.headline,
      experienceYears: tutor.experienceYears,
      rating: tutor.rating,
      subjectsInput: tutor.subjects.join(", "),
      price: tutor.price,
      shortBio: tutor.shortBio,
      fullBio: tutor.fullBio,
      qualifications: tutor.qualifications || "",
      category: tutor.category || "",
      tagsInput: (tutor.tags || []).join(", "),
      availabilityInput: (tutor.availabilitySlots || []).join(", "),
      image: tutor.image || "",
      imageInput: describeImageInput(tutor.image || ""),
    });
    setActiveTab("tutors");
    setMessage(`Editing ${tutor.name}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (tutorId, tutorName) => {
    const confirmed = window.confirm(`Delete tutor \"${tutorName}\"?`);
    if (!confirmed) {
      return;
    }
    deleteTutor(tutorId);
    refresh();
    if (form.id === tutorId) {
      resetForm();
    }
    setMessage(`${tutorName} deleted.`);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const subjects = arrayFromCsv(form.subjectsInput);
    if (!form.name.trim() || !subjects.length || !form.shortBio.trim()) {
      setMessage("Please add tutor name, at least one subject, and a short bio.");
      return;
    }

    const existing = tutors.find((item) => item.id === form.id);
    const tutor = {
      id: form.id || undefined,
      name: form.name.trim(),
      headline:
        form.headline.trim() ||
        `${subjects.slice(0, 2).join(", ")} | ${Number(form.experienceYears) || 1}+ yrs Exp`,
      experienceYears: Number(form.experienceYears) || 1,
      rating: Number(form.rating) || 4.8,
      subjects,
      price: Number(form.price) || 500,
      shortBio: form.shortBio.trim(),
      fullBio: form.fullBio.trim() || form.shortBio.trim(),
      qualifications: form.qualifications.trim() || "Verified tutor",
      category: form.category.trim() || subjects[0],
      tags: arrayFromCsv(form.tagsInput),
      availabilitySlots: arrayFromCsv(form.availabilityInput),
      image: form.image.trim(),
      reviews:
        existing?.reviews || [
          {
            name: "New Student",
            rating: Number(form.rating) || 4.8,
            quote: "Clear explanations and responsive doubt support.",
          },
        ],
    };

    upsertTutor(tutor);
    refresh();
    setMessage(`${tutor.name} saved successfully.`);
    resetForm();
  };

  const handleResetTutors = () => {
    const confirmed = window.confirm("Reset tutors to the curated Dreameer starter set?");
    if (!confirmed) {
      return;
    }
    resetTutorsToSeed();
    refresh();
    resetForm();
    setMessage("Tutor list reset to the curated starter set.");
  };

  const exportRows = (kind) => {
    if (kind === "leads") {
      const success = downloadCsv(
        "dreameer-leads.csv",
        leads.map((item) => ({
          id: item.id,
          studentName: item.studentName,
          mobile: item.mobile,
          tutorName: item.tutorName,
          targetExam: item.targetExam,
          preferredSlot: item.preferredSlot,
          createdAt: formatDateTime(item.createdAt),
          source: item.source,
        })),
      );
      setMessage(success ? "Leads exported as CSV." : "No leads to export yet.");
      return;
    }

    const success = downloadCsv(
      "dreameer-bookings.csv",
      bookings.map((item) => ({
        id: item.id,
        studentName: item.studentName,
        mobile: item.mobile,
        tutorName: item.tutorName,
        targetExam: item.targetExam,
        preferredSlot: item.preferredSlot,
        status: item.status,
        createdAt: formatDateTime(item.createdAt),
        source: item.source,
      })),
    );
    setMessage(success ? "Bookings exported as CSV." : "No bookings to export yet.");
  };

  if (!authenticated) {
    return (
      <section className="surface-panel mx-auto max-w-xl p-8">
        <div className="eyebrow-light">Protected Admin</div>
        <h2 className="mt-4 font-display text-3xl font-extrabold text-ink">Admin login</h2>
        <p className="mt-3 text-sm leading-7 text-slate-600">
          This area is restricted to authorized staff. Enter your admin password to continue.
        </p>
        <form className="mt-6 space-y-4" onSubmit={handleLogin}>
          <label className="space-y-2 text-sm font-semibold text-slate-700">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="form-field"
              placeholder="Enter admin password"
            />
          </label>
          {passwordError ? (
            <div className="rounded-2xl bg-coral-100 px-4 py-3 text-sm text-rose-700">{passwordError}</div>
          ) : null}
          <button type="submit" className="button-primary w-full">
            Unlock Admin
          </button>
        </form>
      </section>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Tutors", value: tutors.length },
          { label: "Leads", value: leads.length },
          { label: "Bookings", value: bookings.length },
        ].map((item) => (
          <div key={item.label} className="surface-panel p-5">
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-400">{item.label}</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-ink">{item.value}</p>
          </div>
        ))}
        <div className="surface-panel p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-400">Access</p>
          <button type="button" className="button-secondary mt-3 w-full" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`rounded-full px-5 py-3 text-sm font-extrabold ${
              activeTab === tab.id
                ? "bg-navy-900 text-white shadow-neon"
                : "border border-slate-200 bg-white text-slate-600"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {message ? (
        <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
          {message}
        </div>
      ) : null}

      {activeTab === "tutors" ? (
        <div className="grid gap-8 xl:grid-cols-[1.08fr_0.92fr]">
          <section className="surface-panel p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="eyebrow-light">Tutor CRUD</div>
                <h3 className="mt-3 font-display text-2xl font-extrabold text-ink">
                  {form.id ? `Editing ${form.name}` : "Add a new tutor"}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Update tutor profiles, change photos, set prices, subjects, ratings, and availability.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" className="button-secondary" onClick={resetForm}>
                  {form.id ? "Cancel Edit" : "Clear Form"}
                </button>
                <button type="button" className="button-secondary" onClick={handleResetTutors}>
                  Reset Demo Tutors
                </button>
              </div>
            </div>

            <div className="mb-6 grid gap-4 rounded-[28px] border border-slate-100 bg-slate-50 p-4 md:grid-cols-[auto_1fr] md:items-center">
              <TutorAvatar
                src={form.image}
                name={form.name || "New Tutor"}
                alt="Tutor preview"
                className="h-24 w-24 rounded-[26px]"
                textClassName="text-3xl"
              />
              <div>
                <p className="text-sm font-extrabold text-slate-700">Live preview</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Upload a photo or paste a remote image URL. If no image is provided, a branded initials avatar is used.
                </p>
              </div>
            </div>

            <form className="grid gap-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Name</span>
                  <input name="name" value={form.name} onChange={handleFormChange} className="form-field" />
                </label>
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Headline</span>
                  <input
                    name="headline"
                    value={form.headline}
                    onChange={handleFormChange}
                    className="form-field"
                    placeholder="Math Specialist | 6 yrs Exp"
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Subjects</span>
                  <input
                    name="subjectsInput"
                    value={form.subjectsInput}
                    onChange={handleFormChange}
                    className="form-field"
                    placeholder="Mathematics, JEE"
                  />
                </label>
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Price / hour</span>
                  <input name="price" type="number" value={form.price} onChange={handleFormChange} className="form-field" />
                </label>
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Rating</span>
                  <input
                    name="rating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={form.rating}
                    onChange={handleFormChange}
                    className="form-field"
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Experience Years</span>
                  <input
                    name="experienceYears"
                    type="number"
                    min="1"
                    value={form.experienceYears}
                    onChange={handleFormChange}
                    className="form-field"
                  />
                </label>
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Category</span>
                  <input
                    name="category"
                    value={form.category}
                    onChange={handleFormChange}
                    className="form-field"
                    placeholder="Science / Commerce / Languages"
                  />
                </label>
              </div>

              <label className="space-y-2 text-sm font-semibold text-slate-700">
                <span>Short Bio</span>
                <textarea name="shortBio" rows="3" value={form.shortBio} onChange={handleFormChange} className="form-field" />
              </label>

              <label className="space-y-2 text-sm font-semibold text-slate-700">
                <span>Full Bio</span>
                <textarea name="fullBio" rows="4" value={form.fullBio} onChange={handleFormChange} className="form-field" />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Qualifications</span>
                  <input name="qualifications" value={form.qualifications} onChange={handleFormChange} className="form-field" />
                </label>
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Tags</span>
                  <input
                    name="tagsInput"
                    value={form.tagsInput}
                    onChange={handleFormChange}
                    className="form-field"
                    placeholder="CBSE, Boards, Mock Tests"
                  />
                </label>
              </div>

              <label className="space-y-2 text-sm font-semibold text-slate-700">
                <span>Availability Slots</span>
                <input
                  name="availabilityInput"
                  value={form.availabilityInput}
                  onChange={handleFormChange}
                  className="form-field"
                  placeholder="Mon | 6:30 PM IST, Wed | 7:45 PM IST"
                />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Image URL</span>
                  <input
                    name="imageInput"
                    value={form.imageInput}
                    onChange={handleFormChange}
                    className="form-field"
                    placeholder="https://example.com/tutor.jpg"
                  />
                </label>
                <label className="space-y-2 text-sm font-semibold text-slate-700">
                  <span>Upload image</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="form-field" />
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <button type="submit" className="button-primary">
                  {form.id ? "Update Tutor" : "Add Tutor"}
                </button>
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => setForm((current) => ({ ...current, image: "", imageInput: "" }))}
                >
                  Remove Image
                </button>
              </div>
            </form>
          </section>

          <section className="space-y-4">
            <div className="surface-panel p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-display text-2xl font-extrabold text-ink">Tutor Directory</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Search, review, edit, and delete tutors currently stored in this browser.
                  </p>
                </div>
                <div className="text-sm font-semibold text-slate-500">{filteredTutors.length} visible tutors</div>
              </div>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="form-field mt-4"
                placeholder="Search tutors in admin..."
              />
            </div>

            {filteredTutors.map((tutor) => (
              <article key={tutor.id} className="surface-panel flex gap-4 p-4">
                <TutorAvatar
                  src={tutor.image}
                  name={tutor.name}
                  alt={`${tutor.name} preview`}
                  className="h-20 w-20 rounded-[24px]"
                  textClassName="text-2xl"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h4 className="font-display text-xl font-extrabold text-ink">{tutor.name}</h4>
                      <p className="text-sm text-slate-600">{tutor.headline}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-extrabold text-emerald-700">{formatCurrency(tutor.price)}/hr</p>
                      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-slate-400">
                        Star {tutor.rating.toFixed(1)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tutor.subjects.slice(0, 3).map((subject) => (
                      <span key={subject} className="chip">
                        {subject}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{tutor.shortBio}</p>
                  <div className="mt-4 flex gap-2">
                    <button type="button" className="button-secondary" onClick={() => handleEdit(tutor)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="rounded-full bg-rose-600 px-5 py-3 text-sm font-extrabold text-white"
                      onClick={() => handleDelete(tutor.id, tutor.name)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </section>
        </div>
      ) : null}

      {activeTab === "leads" ? (
        <section className="surface-panel p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-display text-2xl font-extrabold text-ink">Saved Leads</h3>
              <p className="text-sm text-slate-600">
                Every booking action saves a lead under <code>dreameer_leads</code> in localStorage.
              </p>
            </div>
            <button type="button" className="button-primary" onClick={() => exportRows("leads")}>
              Export CSV
            </button>
          </div>
          <div className="table-shell overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-3">Student</th>
                  <th className="px-4 py-3">Tutor</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Slot</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {leads.length ? (
                  leads.map((lead) => (
                    <tr key={lead.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">{lead.studentName}</td>
                      <td className="px-4 py-3">{lead.tutorName}</td>
                      <td className="px-4 py-3">+91 {lead.mobile}</td>
                      <td className="px-4 py-3">{lead.targetExam}</td>
                      <td className="px-4 py-3">{lead.preferredSlot}</td>
                      <td className="px-4 py-3">{formatDateTime(lead.createdAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-6 text-slate-500" colSpan="6">
                      No leads yet. Submit a booking from the landing page or marketplace.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === "bookings" ? (
        <section className="surface-panel p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-display text-2xl font-extrabold text-ink">Saved Bookings</h3>
              <p className="text-sm text-slate-600">
                Demo bookings are saved locally under <code>dreameer_bookings</code>.
              </p>
            </div>
            <button type="button" className="button-primary" onClick={() => exportRows("bookings")}>
              Export CSV
            </button>
          </div>
          <div className="table-shell overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-3">Student</th>
                  <th className="px-4 py-3">Tutor</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Slot</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length ? (
                  bookings.map((booking) => (
                    <tr key={booking.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">{booking.studentName}</td>
                      <td className="px-4 py-3">{booking.tutorName}</td>
                      <td className="px-4 py-3">+91 {booking.mobile}</td>
                      <td className="px-4 py-3">{booking.targetExam}</td>
                      <td className="px-4 py-3">{booking.preferredSlot}</td>
                      <td className="px-4 py-3">{booking.status}</td>
                      <td className="px-4 py-3">{formatDateTime(booking.createdAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-6 text-slate-500" colSpan="7">
                      No bookings yet. Use the demo flow to generate one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === "security" ? (
        <section className="surface-panel p-6">
          <div className="space-y-5">
            <div>
              <h3 className="font-display text-2xl font-extrabold text-ink">Upgrade security before launch</h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                This starter intentionally keeps admin auth client-side for speed. The README includes a detailed Firebase Auth upgrade path.
              </p>
            </div>
            <ol className="list-decimal space-y-3 pl-5 text-sm leading-7 text-slate-700">
              <li>Create a Firebase project and enable Email/Password authentication.</li>
              <li>Move admin login to Firebase Auth with email/password or SSO.</li>
              <li>Persist tutors, leads, and bookings in Firestore instead of localStorage.</li>
              <li>Protect payment secrets in serverless functions only.</li>
            </ol>
            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm leading-7 text-amber-900">
              Security note: do not store payment secrets, Firebase private keys, or production admin passwords in the client bundle.
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
